import React from "react";
import {
  CheckCircle,
  Shield,
  Activity,
  BookOpen,
  Layers,
  Users,
  FileJson,
} from "lucide-react";

const TabOverviewCard = ({ narrative, iconType = "default" }) => {
  if (!narrative) return null;

  // Icon mapping helper
  const getIcon = () => {
    switch (iconType) {
      case "blueprints":
        return <BookOpen size={20} />;
      case "streams":
        return <Layers size={20} />;
      case "segmentation":
        return <Users size={20} />;
      case "export":
        return <FileJson size={20} />;
      default:
        return <Activity size={20} />;
    }
  };

  // Color theme mapping helper
  const getTheme = () => {
    switch (iconType) {
      case "blueprints":
        return "emerald";
      case "streams":
        return "blue";
      case "segmentation":
        return "purple";
      case "export":
        return "indigo";
      default:
        return "slate";
    }
  };

  const theme = getTheme();

  return (
    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm mb-8 animate-in fade-in slide-in-from-top-4 duration-500">
      {/* Header */}
      <div className="bg-slate-50 p-6 border-b border-slate-200">
        <div className="flex items-center gap-3 mb-2">
          <div className={`p-2 bg-${theme}-100 text-${theme}-700 rounded-lg`}>
            {getIcon()}
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {narrative.title}
            </h2>
            <p className="text-sm font-medium text-slate-500 uppercase tracking-wide">
              {narrative.subtitle}
            </p>
          </div>
        </div>
        <p className="text-slate-700 mt-2 max-w-4xl text-lg leading-relaxed">
          {narrative.executive_overview}
        </p>
      </div>

      {/* Grid Content */}
      <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-slate-100">
        {/* Column 1: What You Get */}
        <div className="p-6">
          <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
            <CheckCircle size={16} className={`text-${theme}-500`} /> What You
            Get
          </h4>
          <ul className="space-y-2">
            {narrative.what_you_get?.map((item, i) => (
              <li
                key={i}
                className="text-sm text-slate-600 flex items-start gap-2"
              >
                <span className="mt-1.5 w-1 h-1 bg-slate-400 rounded-full shrink-0"></span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        {/* Column 2: Why It Matters */}
        <div className="p-6 bg-slate-50/50">
          <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
            <Shield size={16} className="text-amber-500" /> Why It Matters
          </h4>
          <p className="text-sm text-slate-600 leading-relaxed">
            {narrative.why_it_matters}
          </p>
        </div>

        {/* Column 3: Distinction */}
        <div className="p-6">
          <h4 className="font-bold text-slate-900 text-sm mb-3 flex items-center gap-2">
            <Activity size={16} className="text-blue-500" /> Distinction
          </h4>
          <p
            className={`text-sm text-slate-500 italic border-l-2 border-${theme}-200 pl-3`}
          >
            "{narrative.difference}"
          </p>
        </div>
      </div>
    </div>
  );
};

export default TabOverviewCard;
